import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of, throwError } from 'rxjs';
import { Resource } from '../../model/Resource';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {AbstractLiveDataService} from "./abstract-live-data.service";
import {NotificationsService} from "./notifications.service";
import {parseUploadTicket, ResourceUploadTicket} from "./resource-upload";

/** What `POST /resource/upload` gives the caller. */
export interface ResourceUploadResponse {
  /** The row the server just created, already merged into the local cache. */
  resource: Resource;
  /** The presigned POST ticket, or `null` when the server did not send one. */
  upload: ResourceUploadTicket | null;
}

@Injectable()
export class ResourceService extends AbstractLiveDataService<Resource> {

  constructor(private httpa: HttpClient, private messageServicea: MessageService, private startupServicea: StartupService, protected notificationsService: NotificationsService) {
    super(httpa, messageServicea, startupServicea, notificationsService);
    this.notificationsService.setResourceService(this);
    this.notificationsService.startNotification();
  }

  makeObjectInstance(args: any): Resource {
    return new Resource(args);
  }

  getLoggingObjectTypeName (): string {
    return "Resource";
  }

  supportsParallel(): boolean {
    return false;
  }

  getURLEndPoint (): string {
    return "resource";
  }

  getOriginalURL (resourceId: number, filename: string): Observable<string> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/originalURL/${resourceId}/${filename}`;
    return this.http.get(url, this.startupService.getHttpOptions())
      .pipe(
        map(x => {
          return x;
        }),
        catchError(this.handleErrorT<any>('getOriginalURL'))
      );
  }

  getPreparedURL (resourceId: number, filename: string): Observable<string> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/preparedURL/${resourceId}/${filename}`;
    return this.http.get(url, this.startupService.getHttpOptions())
      .pipe(
        map(x => {
          return x;
        }),
        catchError(this.handleErrorT<any>('getPreparedURL'))
      );
  }

  getLinkData(): any[] {
    return [
      { action: 'AddUpdateArray', serviceName: 'bodyService', arrayProperty: 'resource'},
      { action: 'AddUpdateArray', serviceName: 'coverService', arrayProperty: 'resource'},
      { action: 'AddUpdateArray', serviceName: 'draftBodyService', arrayProperty: 'resource'},
      { action: 'AddUpdateArray', serviceName: 'draftCoverService', arrayProperty: 'resource'}
    ];
  }

  getImage (resourceId: number, color: string, dpi: number, page: number): Observable<string> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/image/${resourceId}/${color}/${dpi}/${page}`;
    return this.http.get(url, this.startupService.getHttpOptions())
      .pipe(
        map(image => {
          return image;
        }),
        catchError(this.handleErrorT<any>('getImage'))
      );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleErrorT<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
  public getImageURL(resourceId: number, color: string, dpi: number, page: number): Observable<string> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/imageurl/${resourceId}/${color}/${dpi}/${page}`;
    return this.http.get(url, this.startupService.getHttpOptions())
      .pipe(
        map(image => {
          return image;
        }),
        catchError(this.handleErrorT<any>('getImageURL'))
      );
  }
  public getCroppedImageURL(resourceId: number, color: string, dpi: number, page: number, finalWidth: number, finalHeight: number, horizontalShift: number, verticalShift: number, cropLeft: number, cropTop: number, cropRight: number, cropBottom: number, alignment: 'CENTER_CENTER' | 'TOP_LEFT'): Observable<string> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/croppedimageurl/${resourceId}/${color}/${dpi}/${page}/${Math.ceil(finalWidth)}/${Math.ceil(finalHeight)}/${Math.ceil(horizontalShift)}/${Math.ceil(verticalShift)}/${Math.ceil(cropLeft)}/${Math.ceil(cropTop)}/${Math.ceil(cropRight)}/${Math.ceil(cropBottom)}/${alignment}`;
    return this.http.get(url, this.startupService.getHttpOptions())
      .pipe(
        map(image => {
          return image;
        }),
        catchError(this.handleErrorT<any>('getImageURL'))
      );
  }

  /**
   * Does this session receive `RESOURCE` notifications at all?
   *
   * **`serviceProvider` sessions do not, and that is settled server-side**, not
   * an oversight — confirmed 2026-09-14 in
   * `docs/contracts/inbox/_processed/2026-09-14-serviceprovider-resource-notifications-not-needed.md`
   * and recorded in `shared/Notifications.md` §9. RESOURCE events publish to
   * `network-1` and `organization-<org_ID>` only.
   *
   * Note the contrast with `ECOMMERCE_ORDER`, where the identical-looking
   * omission WAS a bug and was fixed. Do not "correct" this one by analogy.
   */
  public sessionReceivesResourceNotifications(): boolean {
    return this.startupService.selectedRole !== 'serviceProvider';
  }

  /**
   * Poll a Resource until its page count lands, for sessions that will never
   * be told.
   *
   * The preview components refresh when `updateNotifier` fires, which normally
   * happens because a `RESOURCE`/`UPDATE` notification arrived. A
   * serviceProvider session receives no such notification ever, so the server's
   * advice is "read the resource instead" — and that is exactly what this does.
   * Each successful re-fetch runs through `addUpdateLocalItem`, which fires
   * `updateNotifier`, so the existing refresh path does the rest and the
   * components need no special case beyond calling this.
   *
   * The server explicitly said: "if such a session ever sits on a screen that
   * waits for the page count, it will wait forever — so do not build one." We
   * have one whether we like it or not: there are no route guards on
   * `/secure/projects` or `/secure/draftbook/:ID`, and the Projects nav entry
   * is ungated, so the same components serve every role. This is the "do not
   * wait forever" half of honouring that.
   *
   * Bounded deliberately. The schedule mirrors the server's own page-count
   * backoff (5, 5, 10, 15, 30, then 60s) and gives up after roughly six
   * minutes, because a PDF that has not been counted by then is not going to be
   * — `MAX_ATTEMPTS` on their side is ~16 minutes and a failure leaves
   * `numPages` at 0 permanently. Polling past the point of usefulness would
   * just be a background request loop nobody switched off.
   *
   * No-ops when the session does get notifications, so call sites do not have
   * to know the rule.
   */
  private pollingResourceIds = new Set<number>();
  private static readonly RESOURCE_POLL_DELAYS_MS = [5000, 5000, 10000, 15000, 30000, 60000, 60000, 60000, 60000, 60000];

  public ensureProcessedResource(resourceId: number): void {
    if (!resourceId || this.sessionReceivesResourceNotifications()) {
      return;
    }
    if (this.pollingResourceIds.has(resourceId)) {
      return;
    }
    this.pollingResourceIds.add(resourceId);

    const attempt = (index: number) => {
      if (index >= ResourceService.RESOURCE_POLL_DELAYS_MS.length) {
        this.pollingResourceIds.delete(resourceId);
        console.log(`ensureProcessedResource: giving up on ${resourceId} after ${index} attempts`);
        return;
      }
      setTimeout(() => {
        const cached = this.objectsById['' + resourceId];
        if (cached && cached.numPages > 0) {
          // Someone else already refreshed it; updateNotifier has fired.
          this.pollingResourceIds.delete(resourceId);
          return;
        }
        this.getItem(resourceId).subscribe(
          resource => {
            // getItem routes through addUpdateLocalItem, which fires
            // updateNotifier, which is what the previews are listening to.
            if (resource && resource.numPages > 0) {
              this.pollingResourceIds.delete(resourceId);
              return;
            }
            attempt(index + 1);
          },
          err => {
            console.log(`ensureProcessedResource: fetch failed for ${resourceId}`, err);
            attempt(index + 1);
          }
        );
      }, ResourceService.RESOURCE_POLL_DELAYS_MS[index]);
    };

    attempt(0);
  }



  /**
   * Request an upload ticket and a newly created Resource from the server.
   * `POST /resource/upload` — accepts a Resource object, creates the row and
   * returns `{ resource, upload }`.
   * Used for Body, Cover, DraftBody, and DraftCover uploads.
   *
   * ## `upload`, not `uploadURL`
   *
   * `uploadURL` (a presigned **PUT**) is deprecated and the server will delete
   * it once we confirm we are off it. It is not read here at all, deliberately:
   * a silent fallback to the deprecated field is how a migration ends up never
   * finishing, and the whole point of the new ticket is a signed policy that
   * bounds the upload — falling back would discard exactly the protection the
   * change exists to add. When `upload` is absent this yields `upload: null`
   * and the caller reports it. See `docs/contracts/shared/ResourceUploads.md`.
   *
   * ## Errors are deliberately NOT swallowed
   *
   * This used to end with `catchError(this.handleErrorT('getUploadUrl'))`,
   * which turns any failure into `of(undefined)` — so the caller's success
   * branch ran with no result and the caller could not tell a 400 from a 500
   * from a dropped connection. Two statuses now need telling apart by the
   * caller, which that shape made impossible:
   *
   *   400 — `Resources.add` refused the row (role is not `organization`, or
   *         `org_ID` does not match the acting role). Retrying fails
   *         identically.
   *   429 — the organisation's upload allowance for this rolling hour is
   *         spent. **Not retried**, here or by `retryTransient`: the window is
   *         an hour and no backoff we could apply outlives it.
   *
   * There is no `retry` on this call and there must not be one — it is a POST
   * that creates a row and spends an allowance ticket.
   *
   * @param resource A Resource object (at minimum org_ID should be set)
   * @returns Observable of `{ upload, resource }`
   */
  public getUploadUrl(resource: Resource): Observable<ResourceUploadResponse> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/upload`;
    return this.http.post<any>(url, resource.toJSON(), this.startupService.getHttpOptions())
      .pipe(
        map(response => {
          const createdResource = this.makeObjectInstance(response.resource);
          this.addUpdateLocalItem(createdResource);
          return { upload: parseUploadTicket(response), resource: createdResource };
        }),
        catchError((err) => {
          console.error('getUploadUrl failed', err);
          return throwError(err);
        })
      );
  }
}
